// Brukerens egne valg i menyen (etter Amo Ctrl, 18.10.2026): favorittene og
// hvilke grupper som er lagt sammen. Begge er en liste per bruker i
// user_ui_preferences (/api/me/preferences/:key), så de følger brukeren
// mellom maskiner. localStorage er bare en kopi, så menyen ikke blafrer ved
// oppstart. Kopien har bruker-id-en i nøkkelen: på en delt PC på lageret får
// ikke neste bruker forrige brukers favoritter, og ingenting må tømmes ved
// utlogging.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { backendApi } from '../services/api'

export const FAVORITTER = 'meny.favoritter'
export const LUKKEDE_GRUPPER = 'meny.lukket'

const MAKS = 200

const cacheNokkel = (bruker, nokkel) => `pulse.${nokkel}.${bruker}`

const gyldig = v => (Array.isArray(v) ? v.filter(x => typeof x === 'string').slice(0, MAKS) : [])

// Siste verdi i denne fanen. Den gjelder også når nettleseren ikke lar oss
// skrive til localStorage (privat modus, full kvote).
const minne = new Map()

function lesCache(bruker, nokkel) {
  const n = cacheNokkel(bruker, nokkel)
  if (minne.has(n)) return minne.get(n)
  try {
    const raa = localStorage.getItem(n)
    if (raa != null) return gyldig(JSON.parse(raa))
  } catch { /* privat modus eller en ødelagt verdi */ }
  return []
}

function skrivCache(bruker, nokkel, liste) {
  const n = cacheNokkel(bruker, nokkel)
  minne.set(n, liste)
  try { localStorage.setItem(n, JSON.stringify(liste)) } catch { /* privat modus */ }
}

// Lagringene for én nøkkel går etter hverandre. To raske klikk skal ikke kunne
// komme fram i feil rekkefølge, så det første blir stående.
const lagringer = new Map()
function lagre(bruker, nokkel, liste) {
  const n = cacheNokkel(bruker, nokkel)
  const kall = (lagringer.get(n) || Promise.resolve())
    .catch(() => {})
    .then(() => backendApi.put(`/me/preferences/${nokkel}`, { value: liste }))
    .catch(() => {})
  lagringer.set(n, kall)
}

/** Bare for testene. */
export function glemMenyvalg() {
  minne.clear()
  lagringer.clear()
}

/**
 * [liste, settListe] for én nøkkel. `bruker` er app-brukerens id; uten den er
 * lista tom og settListe gjør ingenting. Med `kanLagre: false` («Se som») kan
 * lista endres for visningen, men ingenting skrives, verken lokalt eller på
 * serveren.
 */
export function useMenyvalg(nokkel, { bruker = null, kanLagre = true } = {}) {
  const [tilstand, settTilstand] = useState(() => ({
    bruker, liste: bruker ? lesCache(bruker, nokkel) : [],
  }))
  // Byttes brukeren uten at menyen monteres på nytt, gjelder den nye brukerens
  // kopi fra første tegning.
  const liste = useMemo(
    () => (tilstand.bruker === bruker ? tilstand.liste : (bruker ? lesCache(bruker, nokkel) : [])),
    [tilstand, bruker, nokkel],
  )

  // Teller valgene brukeren har gjort. Et svar fra serveren som kommer etter et
  // klikk, skal ikke skrive over klikket.
  const valg = useRef(0)

  useEffect(() => {
    if (!bruker) return undefined
    let aktiv = true
    const forValg = valg.current
    Promise.resolve()
      .then(() => backendApi.get(`/me/preferences/${nokkel}`, { cache: false }))
      .then(svar => {
        const verdi = svar?.data?.value
        // null: brukeren har ikke valgt noe ennå. Da står kopien.
        if (!aktiv || valg.current !== forValg || !Array.isArray(verdi)) return
        const ny = gyldig(verdi)
        if (kanLagre) skrivCache(bruker, nokkel, ny)
        settTilstand({ bruker, liste: ny })
      })
      .catch(() => {})
    return () => { aktiv = false }
  }, [bruker, nokkel, kanLagre])

  // Lagringen skjer her i klikket og ikke i settTilstand, som React kan kjøre
  // to ganger.
  const settListe = useCallback(neste => {
    if (!bruker) return
    valg.current += 1
    const ny = gyldig(typeof neste === 'function' ? neste(liste) : neste)
    settTilstand({ bruker, liste: ny })
    if (!kanLagre) return
    skrivCache(bruker, nokkel, ny)
    lagre(bruker, nokkel, ny)
  }, [bruker, nokkel, kanLagre, liste])

  return [liste, settListe]
}
